import { timerOutput } from "../../../behavior-timers/protocol.js";
import {
  dateDifferencePart,
  evaluateDateDifference,
} from "../../date-operators.js";
import { objectFactoryId } from "../../../layout/scene-object-types.js";
import { BindingExpression, OperatorNode } from "../../types.js";
import {
  constructObject,
  extendObject,
  selectObjectProperties,
  setObjectProperty,
  objectEntries,
  mapObjectValues,
} from "../../object-operators.js";
import { ExecutionContext, NodeExecutionResult } from "../types.js";
import {
  coerceToBoolean,
  emptyPatchResult,
  makeVisitKey,
  toBorderConfig,
  toBorderRadiusConfig,
  toListValue,
  toObjectValue,
} from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";
import { getLanguage } from "../../../language/dico.js";
import {
  clamp,
  isColorString,
  isFilterString,
  isBoxShadowString,
  isTextShadowString,
  normalizeFontFamilyValue,
  normalizeAnimationDirectionValue,
  normalizeAnimationEasingValue,
  normalizeAnimationFillModeValue,
  normalizeFontWeightValue,
  normalizeTextAlignValue,
  normalizeTextDecorationLineValue,
  normalizeTextDecorationStyleValue,
  normalizeTextTransformValue,
  normalizeWhiteSpaceValue,
  normalizeColorString,
  toColorMixRatio,
  isGradientString,
  evaluateListFind,
  evaluateListSortBy,
  evaluateListFindIndex,
  evaluateListFindByPath,
  evaluateListFindByPathIndex,
  evaluateObjectDefined,
  evaluateObjectEmpty,
  evaluateListAggregateGroupsBy,
  evaluateListAverage,
  evaluateListGroupBy,
  evaluateListMax,
  evaluateListMin,
  evaluateListSum,
  evaluateListTake,
  objectValueToString,
} from "../../method.js";
import { resolvePath } from "../../../layout/index.js";

export class OperatorBehaviorNode extends BehaviorNode {
  declare behavior: OperatorNode;

  constructor(behavior: OperatorNode) {
    super(behavior);
  }

  execute(_context: ExecutionContext): NodeExecutionResult {
    return {
      patch: emptyPatchResult(),
      nextFlows: [],
      scheduledNodes: [],
      runtimeSignals: [],
    };
  }

  getOutput(
    portId: string,
    visiting: Set<string>,
    context: ExecutionContext,
    binding?: boolean,
  ): unknown {
    if (this.behavior.operator === "readTimer") {
      const reference = this.getInput("input1", visiting, context, binding);
      return binding
        ? {
            kind: "unary",
            operator: "readTimer",
            input: reference,
            options: { timerOutputPort: portId },
          }
        : timerOutput(context.data.behaviorTimers, reference, portId);
    }
    // Operators with multiple output ports:
    //   - `comparator` (unary): exposes `outputA` / `outputB` for the pair
    //     of items currently being compared by `sort`.
    //   - `find` / `findByPath` / `findByCondition` (binary / ternary):
    //     expose `output` (matched item) AND `outputIndex` (matched index,
    //     `number`, `-1` if not found).
    // Every other operator exposes a single `output` port; reject anything
    // else early.
    const isComparatorPort = portId === "outputA" || portId === "outputB";
    const isIndexPort = portId === "outputIndex";
    const isDateDifferencePort =
      this.behavior.operator === "dateDifference" &&
      dateDifferencePart(portId) !== undefined;
    if (
      portId !== "output" &&
      !isComparatorPort &&
      !isIndexPort &&
      !isDateDifferencePort
    ) {
      return undefined;
    }

    const visitKey = makeVisitKey(this.behavior.id, portId, context);
    if (visiting.has(visitKey)) {
      return undefined;
    }
    visiting.add(visitKey);

    try {
      const op = this.behavior.operator;
      const mode = this.behavior.mode;
      if (op === "dateDifference") {
        const part = dateDifferencePart(portId);
        if (!part) return undefined;
        const a = this.getInput("input1", visiting, context, binding);
        const b = this.getInput("input2", visiting, context, binding);
        return binding
          ? ({
              kind: "binary",
              operator: op,
              input1: a as BindingExpression,
              input2: b as BindingExpression,
              options: { dateDifferencePart: part },
            } satisfies BindingExpression)
          : evaluateDateDifference(a, b, part);
      }
      if (op === "constructObject") {
        const literal = (value: unknown): BindingExpression => ({
          kind: "literal",
          value,
        });
        const choose = (value: unknown, fallback: unknown): unknown =>
          binding
            ? {
                kind: "ternary",
                operator: "ifElse",
                input1: value,
                input2: fallback,
                input3: {
                  kind: "binary",
                  operator: "notEquals",
                  input1: value,
                  input2: literal(undefined),
                },
              }
            : value !== undefined
              ? value
              : fallback;
        const merge = (base: unknown, addition: unknown): unknown =>
          binding
            ? {
                kind: "binary",
                operator: "extendObject",
                input1: base,
                input2: addition,
              }
            : extendObject(base, addition);
        const factory = (
          typeId: string,
          inputs: Record<string, unknown>,
        ): { found: boolean; value?: unknown } => {
          const id = objectFactoryId(typeId);
          const entry = context.variableRuntimeEntries?.get(id);
          if (!entry) return { found: false };
          const stack = context.variableEvalStack ?? [];
          if (stack.length >= 32 || stack.some((f) => f.variableId === id))
            return {
              found: true,
              value: binding ? literal(undefined) : undefined,
            };
          const end = [...entry.runtime.nodes.values()].find(
            (n) =>
              n.behavior.type === "control" &&
              n.behavior.controlType === "variableEnd",
          );
          return {
            found: true,
            value: end?.getInput(
              "value",
              new Set(),
              {
                ...context,
                variableEvalStack: [
                  ...stack,
                  { variableId: id, refNodeId: this.behavior.id },
                ],
                variableInputFrameStack: [
                  ...(context.variableInputFrameStack ?? []),
                  { inputs },
                ],
              },
              binding,
            ),
          };
        };
        const inputs = Object.fromEntries(
          this.behavior.ports
            .filter((p) => p.direction === "input")
            .map((p) => [
              p.id,
              this.getInput(p.id, visiting, context, binding),
            ]),
        );
        if (!this.behavior.objectDefinition && this.behavior.objectTypeId) {
          const result = factory(this.behavior.objectTypeId, inputs);
          if (result.found) return result.value;
        }
        const supplied = this.behavior.objectDefinition
          ? (context.variableInputFrameStack?.[
              context.variableInputFrameStack.length - 1
            ]?.inputs ?? {})
          : {};
        let base: unknown = binding ? literal({}) : {};
        for (const port of this.behavior.ports) {
          if (port.id !== "base" && !port.id.startsWith("extension:")) continue;
          let value =
            supplied[port.id] === undefined
              ? inputs[port.id]
              : choose(supplied[port.id], inputs[port.id]);
          if (this.behavior.objectDefinition && port.objectTypeId) {
            if (port.objectTypeId.startsWith("source:")) {
              const source = port.objectTypeId.slice(7);
              value = choose(
                value,
                binding
                  ? { kind: "binding", path: source }
                  : (context.data as unknown as Record<string, unknown>)[
                      source
                    ],
              );
            } else {
              const inherited = factory(port.objectTypeId, { base: value });
              if (inherited.found) value = inherited.value;
            }
          }
          base = merge(base, value);
        }
        const fields = this.behavior.ports.filter(
          (p) => p.direction === "input" && p.id.startsWith("field:"),
        );
        if (binding)
          return fields.reduce<BindingExpression>((result, port) => {
            const value =
              supplied[port.id] === undefined
                ? inputs[port.id]
                : choose(supplied[port.id], inputs[port.id]);
            return merge(result, {
              kind: "ternary",
              operator: "ifElse",
              input1: {
                kind: "ternary",
                operator: "setProperty",
                input1: literal({}),
                input2: literal(port.id.slice(6)),
                input3: value,
              },
              input2: literal({}),
              input3: {
                kind: "binary",
                operator: "notEquals",
                input1: value,
                input2: literal(undefined),
              },
            }) as BindingExpression;
          }, base as BindingExpression);
        return constructObject(
          base,
          fields.map((p) => [
            p.id.slice(6),
            supplied[p.id] !== undefined ? supplied[p.id] : inputs[p.id],
          ]),
        );
      }
      if ((op === "mapItems" || op === "mapValues") && !binding) {
        const source = this.getInput("input1", visiting, context);
        const edge = this.incomingEdgesByPort.get("input2")?.[0];
        const transform = (item: unknown, index: number) =>
          edge
            ? edge.fromNode.getOutput(edge.fromPort, new Set(), {
                ...context,
                iterationScopes: [
                  ...(context.iterationScopes ?? []),
                  { list: source, item },
                ],
                data: {
                  ...context.data,
                  iterationItem: item,
                  iterationIndex: index,
                },
              })
            : this.getInput("input2", visiting, context);
        return op === "mapItems"
          ? toListValue(source).map(transform)
          : mapObjectValues(source, (value, key, index) =>
              transform({ key, value }, index),
            );
      }

      if (mode === "unary") {
        // `currentItem` reads the per-iteration item from the runtime context.
        // It does NOT consume `input1` (the list connection is only used by
        // the editor to type the output).
        //
        // The iteration scope key is `iterationItem` (with `iterationIndex`),
        // dedicated to generic list-operator iteration (`findByCondition`,
        // future map/filter operators, …). It is intentionally distinct from
        // `layoutListRowItem` / `rowIndex` which are owned by the `LayoutList`
        // component for its row context (variable "Current row", `onChange`
        // events, `setFilter` / `setSort` actions). This avoids any shadowing
        // when `findByCondition` is used inside a `LayoutList` behavior.
        //
        // - Runtime: returns `context.data.iterationItem` (optionally
        //   resolving `objectFieldPath`).
        // - Binding: returns a `BindingPath` that points to `iterationItem`
        //   so `findByCondition`'s binding resolver can fill it per-iteration
        //   when re-evaluating the predicate sub-expression.
        if (op === "currentItem" || op === "currentEntry") {
          if (binding) {
            return {
              kind: "unary",
              operator: op,
              input: this.getInput("input1", visiting, context, true),
              options: { objectFieldPath: this.behavior.objectFieldPath },
            } as BindingExpression;
          }
          const list = context.iterationScopes?.length
            ? this.getInput("input1", visiting, context)
            : undefined;
          const scope =
            list === undefined
              ? undefined
              : context.iterationScopes
                  ?.slice()
                  .reverse()
                  .find((scope) => scope.list === list);
          const item = scope ? scope.item : context.data.iterationItem;
          if (item == null) {
            return undefined;
          }
          if (this.behavior.objectFieldPath) {
            return resolvePath(item, this.behavior.objectFieldPath);
          }
          return item;
        }

        // `comparator` is a unary node with TWO output ports (`outputA` and
        // `outputB`). It exposes the pair currently being compared by `sort`,
        // by reading `iterationItemA` / `iterationItemB` from the per-pair
        // scope injected by the `sort` operator. Like `currentItem`, the
        // input list is only used for editor typing.
        if (op === "comparator") {
          const which = portId === "outputB" ? "B" : "A";
          const scopeKey = which === "A" ? "iterationItemA" : "iterationItemB";
          if (binding) {
            const path = this.behavior.objectFieldPath
              ? `${scopeKey}.${this.behavior.objectFieldPath}`
              : scopeKey;
            return { kind: "binding", path } as BindingExpression;
          }
          const item =
            which === "A"
              ? context.data.iterationItemA
              : context.data.iterationItemB;
          if (item == null) {
            return undefined;
          }
          if (this.behavior.objectFieldPath) {
            return resolvePath(item, this.behavior.objectFieldPath);
          }
          return item;
        }

        const v = this.getInput("input1", visiting, context, binding);
        if (binding) {
          if (op === "get") {
            return {
              kind: "binary",
              operator: op,
              input1: v as BindingExpression,
              input2: { kind: "literal", value: this.behavior.objectFieldPath },
            } satisfies BindingExpression;
          }
          return {
            kind: "unary",
            operator: op,
            input: v as BindingExpression,
            options: {
              datePartTextFormat: this.behavior.datePartTextFormat,
              input1ValueType: this.getInputValueType("input1"),
            },
          } as BindingExpression;
        }
        if (op === "objectKeys")
          return objectEntries(v).map((item) => item.key);
        if (op === "objectValues")
          return objectEntries(v).map((item) => item.value);
        if (op === "objectEntries") return objectEntries(v);

        if (op === "defined") {
          if (this.getInputValueType("input1") === "object") {
            return evaluateObjectDefined(v);
          }
          return (
            v != null && v !== "" && !(typeof v === "number" && Number.isNaN(v))
          );
        }
        if (op === "empty") {
          if (this.getInputValueType("input1") === "object") {
            return evaluateObjectEmpty(v);
          }
          if (Array.isArray(v)) {
            return v.length === 0;
          }
          return (
            v == null || v === "" || (typeof v === "number" && Number.isNaN(v))
          );
        }
        if (op === "not") {
          return !coerceToBoolean(v);
        }
        if (op === "length") {
          return toListValue(v).length;
        }
        if (op === "get") {
          if (v == null || typeof v !== "object") {
            return undefined;
          }
          if (!this.behavior.objectFieldPath) {
            return undefined;
          }
          return resolvePath(v, this.behavior.objectFieldPath);
        }
        if (op === "isNotEmpty") {
          return toListValue(v).length > 0;
        }
        if (op === "ceil") return Math.ceil(Number(v));
        if (op === "sum") {
          return evaluateListSum(v);
        }
        if (op === "average") {
          return evaluateListAverage(v);
        }
        if (op === "min") {
          return evaluateListMin(v);
        }
        if (op === "max") {
          return evaluateListMax(v);
        }
        if (op === "factorial") {
          const n = Number(v);
          if (!Number.isFinite(n) || n < 0) return 0;
          let result = 1;
          for (let i = 2; i <= Math.floor(n); i++) result *= i;
          return result;
        }

        const locale = getLanguage();
        if (op === "castToString") {
          if (this.getInputValueType("input1") === "object") {
            return objectValueToString(v);
          }
          return this.getInputValueType("input1") === "date"
            ? new Date(String(v)).toLocaleDateString(locale)
            : String(v);
        }
        if (op === "castToNumber") {
          return Number(v);
        }
        if (op === "castToFontFamily") {
          return normalizeFontFamilyValue(v);
        }
        if (op === "castToFontWeight") {
          return normalizeFontWeightValue(v);
        }
        if (op === "castToTextAlign") {
          return normalizeTextAlignValue(v);
        }
        if (op === "castToTextTransform") {
          return normalizeTextTransformValue(v);
        }
        if (op === "castToWhiteSpace") {
          return normalizeWhiteSpaceValue(v);
        }
        if (op === "castToTextDecorationLine") {
          return normalizeTextDecorationLineValue(v);
        }
        if (op === "castToTextDecorationStyle") {
          return normalizeTextDecorationStyleValue(v);
        }
        if (op === "castToBoolean") {
          if (typeof v === "string") {
            const normalized = v.trim().toLowerCase();
            if (normalized === "true" || normalized === "1") return true;
            if (
              normalized === "false" ||
              normalized === "0" ||
              normalized === ""
            )
              return false;
          }
          return Boolean(v);
        }
        if (op === "castToColor") {
          const candidate = String(v ?? "").trim();
          return isColorString(candidate) ? candidate : "#00000000";
        }
        if (op === "castToGradient") {
          const candidate = String(v ?? "").trim();
          return isGradientString(candidate)
            ? candidate
            : "linear-gradient(90deg, #000000 0%, #ffffff 100%)";
        }
        if (op === "castToFilter") {
          const candidate = String(v ?? "").trim();
          return isFilterString(candidate) ? candidate : "none";
        }
        if (op === "castToBoxShadow") {
          const candidate = String(v ?? "").trim();
          return isBoxShadowString(candidate) ? candidate : "none";
        }
        if (op === "castToTextShadow") {
          const candidate = String(v ?? "").trim();
          return isTextShadowString(candidate) ? candidate : "none";
        }
        if (op === "castToBorder") {
          return toBorderConfig(v);
        }
        if (op === "castToBorderRadius") {
          return toBorderRadiusConfig(v);
        }
        if (op === "castToObject") {
          return toObjectValue(v);
        }
        if (op === "toUpperCase") {
          return String(v).toUpperCase();
        }
        if (op === "toLowerCase") {
          return String(v).toLowerCase();
        }
        if (op === "capitalize") {
          return String(v).charAt(0).toUpperCase() + String(v).slice(1);
        }

        if (op === "getDay") {
          const date = new Date(String(v));
          const df = this.behavior.datePartTextFormat;
          if (!df || df === "numeric") {
            return date.getDay();
          }
          if (df === "narrow" || df === "short" || df === "long") {
            return date.toLocaleDateString(locale, { weekday: df });
          }
          return date.toLocaleDateString(locale, { day: df });
        }
        if (op === "getMonth") {
          const date = new Date(String(v));
          const df = this.behavior.datePartTextFormat;
          if (!df || df === "numeric") {
            return date.getMonth();
          }
          return date.toLocaleDateString(locale, { month: df });
        }
        if (op === "getYear") {
          return new Date(String(v)).getFullYear();
        }
        if (op === "getHour") {
          return new Date(String(v)).getHours();
        }
        if (op === "getMinute") {
          return new Date(String(v)).getMinutes();
        }
        if (op === "getSecond") {
          return new Date(String(v)).getSeconds();
        }
        return false;
      }

      // `aggregateGroups` evaluates its connected numeric expression once per
      // item, exposing that item through the generic iteration scope.
      if (mode === "binary" && op === "aggregateGroups" && !binding) {
        const groups = this.getInput("input1", visiting, context, binding);
        const incomingValueEdge = this.incomingEdgesByPort.get("input2")?.[0];

        return evaluateListAggregateGroupsBy(groups, (item, itemIndex) => {
          if (!incomingValueEdge) {
            return this.getInput("input2", visiting, context, binding);
          }
          const scopedContext: ExecutionContext = {
            ...context,
            data: {
              ...context.data,
              iterationItem: item,
              iterationIndex: itemIndex,
            },
          };
          return incomingValueEdge.fromNode.getOutput(
            incomingValueEdge.fromPort,
            new Set<string>(),
            scopedContext,
          );
        });
      }

      // `findByCondition` re-evaluates the boolean predicate per list item by
      // injecting `iterationItem` and `iterationIndex` into the scoped context.
      // These keys remain distinct from `layoutListRowItem` / `rowIndex`.
      if (mode === "binary" && op === "findByCondition" && !binding) {
        const list = toListValue(
          this.getInput("input1", visiting, context, binding),
        );
        const incomingPredicateEdge =
          this.incomingEdgesByPort.get("input2")?.[0];
        // `findByCondition` exposes two output ports: `output` (matched item)
        // and `outputIndex` (matched 0-based index, `-1` if none).
        const wantsIndex = portId === "outputIndex";

        if (!incomingPredicateEdge) {
          // No upstream graph wired: fall back to the literal port value.
          const fallback = this.getInput("input2", visiting, context, binding);
          const truthy = coerceToBoolean(fallback);
          if (wantsIndex) return truthy ? 0 : -1;
          return truthy ? list[0] : undefined;
        }

        for (let index = 0; index < list.length; index += 1) {
          const item = list[index];
          const scopedData = {
            ...context.data,
            iterationItem: item,
            iterationIndex: index,
          };
          const scopedContext: ExecutionContext = {
            ...context,
            data: scopedData,
          };
          const predicateResult = incomingPredicateEdge.fromNode.getOutput(
            incomingPredicateEdge.fromPort,
            new Set<string>(),
            scopedContext,
          );
          if (coerceToBoolean(predicateResult)) {
            return wantsIndex ? index : item;
          }
        }
        return wantsIndex ? -1 : undefined;
      }

      // `filter` shares the same per-item iteration scope as `findByCondition`
      // (`iterationItem` / `iterationIndex`) but returns ALL matching items.
      if (mode === "binary" && op === "filter" && !binding) {
        const list = toListValue(
          this.getInput("input1", visiting, context, binding),
        );
        const incomingPredicateEdge =
          this.incomingEdgesByPort.get("input2")?.[0];

        if (!incomingPredicateEdge) {
          // No upstream graph wired: literal predicate applies uniformly.
          const fallback = this.getInput("input2", visiting, context, binding);
          return coerceToBoolean(fallback) ? [...list] : [];
        }

        const result: unknown[] = [];
        for (let index = 0; index < list.length; index += 1) {
          const item = list[index];
          const scopedData = {
            ...context.data,
            iterationItem: item,
            iterationIndex: index,
          };
          const scopedContext: ExecutionContext = {
            ...context,
            data: scopedData,
          };
          const predicateResult = incomingPredicateEdge.fromNode.getOutput(
            incomingPredicateEdge.fromPort,
            new Set<string>(),
            scopedContext,
          );
          if (coerceToBoolean(predicateResult)) {
            result.push(item);
          }
        }
        return result;
      }

      if (mode === "binary" && op === "sortBy" && !binding) {
        const list = this.getInput("input1", visiting, context);
        const keyEdge = this.incomingEdgesByPort.get("input2")?.[0];
        if (!keyEdge) return [...toListValue(list)];
        return evaluateListSortBy(list, (item, index) =>
          keyEdge.fromNode.getOutput(keyEdge.fromPort, new Set<string>(), {
            ...context,
            iterationScopes: [
              ...(context.iterationScopes ?? []),
              { list, item },
            ],
            data: {
              ...context.data,
              iterationItem: item,
              iterationIndex: index,
            },
          }),
        );
      }

      // `sort` evaluates a numeric comparator (negative ⇒ A before B,
      // zero ⇒ equal, positive ⇒ B before A) per pair, by injecting
      // `iterationItemA` / `iterationItemB` into the scoped context. The
      // comparator graph reads these via the `comparator` operator.
      if (mode === "binary" && op === "sort" && !binding) {
        const list = toListValue(
          this.getInput("input1", visiting, context, binding),
        );
        const incomingComparatorEdge =
          this.incomingEdgesByPort.get("input2")?.[0];

        if (!incomingComparatorEdge) {
          // No upstream graph wired: comparator is a constant ⇒ stable list.
          return [...list];
        }

        // We sort indices then map back to items so the original list isn't
        // mutated and ties remain stable on identical comparator results.
        const indices = list.map((_, index) => index);
        indices.sort((aIndex, bIndex) => {
          const itemA = list[aIndex];
          const itemB = list[bIndex];
          const scopedData = {
            ...context.data,
            iterationItemA: itemA,
            iterationItemB: itemB,
          };
          const scopedContext: ExecutionContext = {
            ...context,
            data: scopedData,
          };
          const result = incomingComparatorEdge.fromNode.getOutput(
            incomingComparatorEdge.fromPort,
            new Set<string>(),
            scopedContext,
          );
          const numeric = Number(result);
          if (!Number.isFinite(numeric) || numeric === 0) {
            // Stable tie-breaker on original index.
            return aIndex - bIndex;
          }
          return numeric;
        });
        return indices.map((index) => list[index]);
      }

      const a = this.getInput("input1", visiting, context, binding);
      const b = this.getInput("input2", visiting, context, binding);

      if (binding) {
        // Multi-output list operators encode the requested port via
        // `options.outputKind` so the binding resolver can return either the
        // matched item or its 0-based index from the same expression.
        const wantsIndex = portId === "outputIndex";
        if (mode === "binary") {
          if (op === "find") {
            return {
              kind: "binary",
              operator: op,
              input1: a as BindingExpression,
              input2: b as BindingExpression,
              options: {
                objectFieldPath: this.behavior.objectFieldPath,
                ...(wantsIndex ? { outputKind: "index" as const } : {}),
              },
            } as BindingExpression;
          }
          if (op === "findByCondition") {
            return {
              kind: "binary",
              operator: op,
              input1: a as BindingExpression,
              input2: b as BindingExpression,
              ...(wantsIndex
                ? { options: { outputKind: "index" as const } }
                : {}),
            } as BindingExpression;
          }
          return {
            kind: "binary",
            operator: op,
            input1: a as BindingExpression,
            input2: b as BindingExpression,
            ...((op === "equals" || op === "notEquals") &&
            this.getInputValueType("input1") === "boolean"
              ? { options: { input1ValueType: "boolean" } }
              : {}),
          } as BindingExpression;
        }
        if (mode === "ternary") {
          const c = this.getInput("input3", visiting, context, binding);
          if (op === "findByPath") {
            return {
              kind: "ternary",
              operator: op,
              input1: a as BindingExpression,
              input2: b as BindingExpression,
              input3: c as BindingExpression,
              ...(wantsIndex
                ? { options: { outputKind: "index" as const } }
                : {}),
            } as BindingExpression;
          }
          return {
            kind: "ternary",
            operator: op,
            input1: a as BindingExpression,
            input2: b as BindingExpression,
            input3: c as BindingExpression,
          } as BindingExpression;
        }
      }

      if (op === "extendObject") return extendObject(a, b);
      if (op === "pickObject") return selectObjectProperties(a, b);
      if (op === "omitObject") return selectObjectProperties(a, b, true);
      if (op === "setProperty")
        return setObjectProperty(
          a,
          b,
          this.getInput("input3", visiting, context),
        );
      switch (op) {
        case "and":
          return coerceToBoolean(a) && coerceToBoolean(b);
        case "or":
          return coerceToBoolean(a) || coerceToBoolean(b);

        case "equals": {
          if (this.getInputValueType("input1") === "boolean") {
            return coerceToBoolean(a) === coerceToBoolean(b);
          }
          if (this.getInputValueType("input1") === "list") {
            return (
              JSON.stringify(toListValue(a)) === JSON.stringify(toListValue(b))
            );
          }
          if (this.getInputValueType("input1") === "color") {
            return normalizeColorString(a) === normalizeColorString(b);
          }
          if (this.getInputValueType("input1") === "animationDirection") {
            return (
              normalizeAnimationDirectionValue(a) ===
              normalizeAnimationDirectionValue(b)
            );
          }
          if (this.getInputValueType("input1") === "animationFill") {
            return (
              normalizeAnimationFillModeValue(a) ===
              normalizeAnimationFillModeValue(b)
            );
          }
          if (this.getInputValueType("input1") === "animationEasing") {
            return (
              normalizeAnimationEasingValue(a) ===
              normalizeAnimationEasingValue(b)
            );
          }
          if (this.getInputValueType("input1") === "fontFamily") {
            return normalizeFontFamilyValue(a) === normalizeFontFamilyValue(b);
          }
          if (this.getInputValueType("input1") === "fontWeight") {
            return normalizeFontWeightValue(a) === normalizeFontWeightValue(b);
          }
          if (this.getInputValueType("input1") === "textAlign") {
            return normalizeTextAlignValue(a) === normalizeTextAlignValue(b);
          }
          if (this.getInputValueType("input1") === "textTransform") {
            return (
              normalizeTextTransformValue(a) === normalizeTextTransformValue(b)
            );
          }
          if (this.getInputValueType("input1") === "whiteSpace") {
            return normalizeWhiteSpaceValue(a) === normalizeWhiteSpaceValue(b);
          }

          return a === b || Number(a) === Number(b);
        }
        case "notEquals": {
          if (this.getInputValueType("input1") === "boolean") {
            return coerceToBoolean(a) !== coerceToBoolean(b);
          }
          if (this.getInputValueType("input1") === "list") {
            return (
              JSON.stringify(toListValue(a)) !== JSON.stringify(toListValue(b))
            );
          }
          if (this.getInputValueType("input1") === "color") {
            return normalizeColorString(a) !== normalizeColorString(b);
          }
          if (this.getInputValueType("input1") === "animationDirection") {
            return (
              normalizeAnimationDirectionValue(a) !==
              normalizeAnimationDirectionValue(b)
            );
          }
          if (this.getInputValueType("input1") === "animationFill") {
            return (
              normalizeAnimationFillModeValue(a) !==
              normalizeAnimationFillModeValue(b)
            );
          }
          if (this.getInputValueType("input1") === "animationEasing") {
            return (
              normalizeAnimationEasingValue(a) !==
              normalizeAnimationEasingValue(b)
            );
          }
          if (this.getInputValueType("input1") === "fontFamily") {
            return normalizeFontFamilyValue(a) !== normalizeFontFamilyValue(b);
          }
          if (this.getInputValueType("input1") === "fontWeight") {
            return normalizeFontWeightValue(a) !== normalizeFontWeightValue(b);
          }
          if (this.getInputValueType("input1") === "textAlign") {
            return normalizeTextAlignValue(a) !== normalizeTextAlignValue(b);
          }
          if (this.getInputValueType("input1") === "textTransform") {
            return (
              normalizeTextTransformValue(a) !== normalizeTextTransformValue(b)
            );
          }
          if (this.getInputValueType("input1") === "whiteSpace") {
            return normalizeWhiteSpaceValue(a) !== normalizeWhiteSpaceValue(b);
          }
          return a !== b && Number(a) !== Number(b);
        }
        case "greaterThan":
          return Number(a) > Number(b);
        case "lessThan":
          return Number(a) < Number(b);
        case "greaterThanOrEqual":
          return Number(a) >= Number(b);
        case "lessThanOrEqual":
          return Number(a) <= Number(b);
        case "contains":
          if (this.getInputValueType("input1") === "list") {
            const serializedNeedle = String(b ?? "");
            return toListValue(a).some(
              (item) =>
                String(item) === serializedNeedle ||
                JSON.stringify(item) === serializedNeedle,
            );
          }
          return String(a ?? "").includes(String(b ?? ""));
        case "notContains":
          return !String(a ?? "").includes(String(b ?? ""));
        case "startsWith":
          return String(a ?? "").startsWith(String(b ?? ""));
        case "endsWith":
          return String(a ?? "").endsWith(String(b ?? ""));

        case "add":
          return Number(a) + Number(b);
        case "subtract":
          return Number(a) - Number(b);
        case "multiply":
          return Number(a) * Number(b);
        case "divide": {
          const divisor = Number(b);
          return divisor === 0 ? 0 : Number(a) / divisor;
        }
        case "modulo": {
          const mod = Number(b);
          return mod === 0 ? 0 : Number(a) % mod;
        }
        case "power":
          return Math.pow(Number(a), Number(b));

        case "round": {
          const value = Number(a);
          const rawDecimals = Number(b);
          if (!Number.isFinite(value)) {
            return Number.NaN;
          }
          const decimals = Number.isFinite(rawDecimals)
            ? Math.max(0, Math.min(20, Math.floor(rawDecimals)))
            : 0;
          const factor = 10 ** decimals;

          return Math.round(value * factor) / factor;
        }

        case "concat":
          return String(a ?? "") + String(b ?? "");
        case "chartAt": {
          const source = String(a ?? "");
          const index = Number(b);
          if (!Number.isInteger(index) || index < 0 || index >= source.length) {
            return "";
          }
          return source.charAt(index);
        }
        case "compare":
          return String(a ?? "").localeCompare(String(b ?? ""), getLanguage(), {
            sensitivity: "base",
            numeric: true,
          });
        case "join": {
          return toListValue(a).join(String(b ?? ","));
        }
        case "push": {
          return [...toListValue(a), b];
        }
        case "remove": {
          const source = [...toListValue(a)];
          const index = Number(b);
          if (!Number.isInteger(index) || index < 0 || index >= source.length) {
            return source;
          }
          source.splice(index, 1);
          return source;
        }
        case "take":
          return evaluateListTake(a, b);
        case "get": {
          const source = toListValue(a);
          const index = Number(b);
          if (!Number.isInteger(index) || index < 0 || index >= source.length) {
            return undefined;
          }
          return source[index];
        }
        case "map": {
          const source = toListValue(a);
          const path = String(b ?? "").trim();
          if (!path) {
            return source;
          }
          return source.map((item) => resolvePath(item, path));
        }
        case "groupBy":
          return evaluateListGroupBy(a, b);
        case "aggregateGroups":
          return evaluateListAggregateGroupsBy(a, () => b);
        case "find":
          // Dispatch on `portId`: `outputIndex` returns the matched 0-based
          // index (`-1` if none); `output` returns the matched item.
          return portId === "outputIndex"
            ? evaluateListFindIndex(a, b, this.behavior.objectFieldPath)
            : evaluateListFind(a, b, this.behavior.objectFieldPath);
        case "findByPath": {
          const expected = this.getInput("input3", visiting, context, binding);
          return portId === "outputIndex"
            ? evaluateListFindByPathIndex(a, b, expected)
            : evaluateListFindByPath(a, b, expected);
        }
        case "getByPath": {
          if (a == null || typeof a !== "object") {
            return "";
          }
          const path = String(b ?? "").trim();
          if (!path) {
            return "";
          }
          const resolved = resolvePath(a, path);
          return resolved == null ? "" : String(resolved);
        }
        case "replace": {
          const replacement = this.getInput("input3", visiting, context);
          return String(a ?? "").replace(
            String(b ?? ""),
            String(replacement ?? ""),
          );
        }
        case "boolToSign": {
          const whenFalse = this.getInput("input3", visiting, context);
          return coerceToBoolean(a) ? Number(b) : Number(whenFalse);
        }
        case "ifElse": {
          const condition = this.getInput("input3", visiting, context);
          return coerceToBoolean(condition) ? a : b;
        }
        case "withAlpha": {
          const color = String(a ?? "").trim();
          if (!isColorString(color)) {
            return "";
          }
          const alpha = clamp(Number(b), 0, 1);
          const opaquePercent = Math.round((1 - alpha) * 100);
          return `color-mix(in srgb, transparent ${opaquePercent}%, ${color})`;
        }
        case "mix": {
          const c1 = String(a ?? "").trim();
          const c2 = String(b ?? "").trim();
          const input3 = this.getInput("input3", visiting, context);
          if (!isColorString(c1) || !isColorString(c2)) {
            return "";
          }
          const ratio = toColorMixRatio(input3);
          const leftPercent = Math.round(100 - ratio);
          const rightPercent = Math.round(ratio);
          return `color-mix(in srgb, ${c1} ${leftPercent}%, ${c2} ${rightPercent}%)`;
        }
        case "isSame":
          return (
            new Date(String(a)).getTime() === new Date(String(b)).getTime()
          );
        case "isAfter":
          return new Date(String(a)).getTime() > new Date(String(b)).getTime();
        case "isBefore":
          return new Date(String(a)).getTime() < new Date(String(b)).getTime();
        case "addDays":
          return new Date(
            new Date(String(a)).getTime() + Number(b) * 24 * 60 * 60 * 1000,
          );
        case "addMonths":
          return new Date(
            new Date(String(a)).getTime() +
              Number(b) * 30 * 24 * 60 * 60 * 1000,
          );
        case "addYears":
          return new Date(
            new Date(String(a)).getTime() +
              Number(b) * 365 * 24 * 60 * 60 * 1000,
          );
        case "addHours":
          return new Date(
            new Date(String(a)).getTime() + Number(b) * 60 * 60 * 1000,
          );
        case "addMinutes":
          return new Date(
            new Date(String(a)).getTime() + Number(b) * 60 * 1000,
          );
        case "addSeconds":
          return new Date(new Date(String(a)).getTime() + Number(b) * 1000);

        default:
          return false;
      }
    } finally {
      visiting.delete(visitKey);
    }
  }
}
